import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environment/environment';
import { SessionsService } from '../../besharp-sessions/lib/services/sessions.service';
import { buildSessionHeaders } from '../../besharp-sessions/lib/utils/interceptor';

const PLOTLY_CDN_URL = 'https://cdn.plot.ly/plotly-2.35.3.min.js';

// Height bounds for the chart box, in px. The figure is written by a model, which sizes it
// for a page rather than for a chat bubble — a 27-slice pie arrives declaring four figures
// of height. Keep the default in step with the `.plotly-diagram` height in the component
// stylesheet, which is what the skeleton is drawn at before the figure resolves.
const DEFAULT_CHART_HEIGHT = 450;
const MIN_CHART_HEIGHT = 320;
const MAX_CHART_HEIGHT = 900;

// A chart is one message in a conversation, not a page of its own: past roughly two thirds of
// the window it stops sharing the screen with the answer it belongs to and with the composer,
// and reading it means scrolling away from both. Anything that needs more room than this gets
// it from the expand control instead.
const MAX_VIEWPORT_SHARE = 0.66;

/**
 * Service to render Plotly charts in the DOM.
 * Charts are identified by .plotly-diagram[data-plotly-src].
 *
 * The attribute holds the encodeURIComponent'd body of a ```plotly fence, which is one of
 * two things: a reference `{"chart_id": "..."}` for anything generated since the figure
 * stopped travelling through the LLMs, or a whole fig.to_json() for answers written before
 * that. The inline branch is permanent, not transitional — those answers are stored in the
 * conversation history and have to keep rendering.
 *
 * Plotly is NOT bundled by esbuild (breaks the build) and is NOT loaded via a
 * <script> in index.html — a blocking classic script there executes before the
 * Angular polyfills (zone.js), which must load first or change detection breaks.
 * Instead it is injected lazily from here the first time a chart needs drawing.
 */
@Injectable({
  providedIn: 'root',
})
export class PlotlyService {
  private sessionsService = inject(SessionsService);
  private plotlyLoad?: Promise<void>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private get Plotly(): any {
    return (window as any)['Plotly'];
  }

  /** Injects the Plotly CDN script once; resolves when window.Plotly is ready. */
  private ensurePlotlyLoaded(): Promise<void> {
    if (this.Plotly) return Promise.resolve();
    if (this.plotlyLoad) return this.plotlyLoad;

    this.plotlyLoad = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = PLOTLY_CDN_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        this.plotlyLoad = undefined; // allow a later retry
        reject(new Error('Failed to load Plotly from CDN'));
      };
      document.head.appendChild(script);
    });

    return this.plotlyLoad;
  }

  /**
   * Resolve a fence body to a drawable figure, fetching it when the fence only carries an id.
   */
  private async resolveSpec(spec: any): Promise<any> {
    if (!spec || typeof spec !== 'object' || spec.data || !spec.chart_id) {
      return spec;
    }

    // The API authenticates on a Cognito bearer token, not on the session cookie, so the
    // request has to carry the same headers every other call builds — `credentials` alone
    // gets back "Authentication token is missing".
    const baseUrl = environment.backend.endpoint || '/api';
    const response = await fetch(`${baseUrl}/chart/${encodeURIComponent(spec.chart_id)}`, {
      headers: new Headers(await buildSessionHeaders(this.sessionsService)),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Chart ${spec.chart_id} could not be fetched (${response.status})`);
    }
    return response.json();
  }

  /**
   * A modebar button that takes the chart full screen, and back.
   *
   * Plotly derives the modebar from what the trace type supports, and a pie supports none of
   * the zoom/pan/reset family — so the bar collapses to the download button alone and there is
   * nothing to do with a dense chart but squint at it. Expanding is the control that is
   * missing, and unlike the others it means the same thing for every chart type.
   */
  private expandButton() {
    return {
      name: 'expand',
      title: 'Expand to full screen',
      // Plotly's own four-arrows glyph. Reusing it keeps the button visually part of the bar
      // rather than a foreign icon sitting next to Plotly's.
      icon: this.Plotly.Icons.autoscale,
      click: (gd: HTMLElement) => {
        if (document.fullscreenElement === gd) {
          void document.exitFullscreen();
        } else {
          void gd.requestFullscreen?.();
        }
      },
    };
  }

  /**
   * Keep the figure's size in step with entering and leaving full screen.
   *
   * Both dimensions need handling, for different reasons. `layout.height` is a fixed number,
   * so on its own the chart would get the whole screen to sit in and keep drawing itself at
   * chat height. Width is autosized, but autosizing is resolved when the figure is drawn and
   * cached — a relayout that only names the height leaves the cached width in place, and the
   * chart renders at its old width against the left edge of the screen with the rest blank.
   * Clearing the width and resizing is what makes Plotly measure the element again.
   *
   * The work is deferred a frame: `fullscreenchange` fires before the element has been resized
   * to the screen, so measuring during the event reads the size it is leaving behind.
   */
  private bindFullscreen(el: HTMLElement, boxHeight: number): void {
    if (el.dataset['fullscreenBound']) return;
    el.dataset['fullscreenBound'] = '1';

    el.addEventListener('fullscreenchange', () => {
      const expanded = document.fullscreenElement === el;
      requestAnimationFrame(() => {
        this.Plotly.relayout(el, { height: expanded ? window.innerHeight : boxHeight, width: null });
        this.Plotly.Plots.resize(el);
      });
    });
  }

  /**
   * The pie traces that write their text outside the circle.
   *
   * This is the shape that fights everything else in the figure. Outside text is not bounded
   * by the plot area the way an axis-bounded trace is — it runs past the paper and is stopped
   * only by the edge of the SVG — so it reaches into the strip the legend uses and into the
   * band the title uses, whatever margins the layout asks for.
   */
  private outsideTextPies(data: unknown): Record<string, unknown>[] {
    if (!Array.isArray(data)) return [];

    return (data as Record<string, unknown>[]).filter((trace) => {
      if (!trace || trace['type'] !== 'pie') return false;

      // Plotly accepts one position for the trace or one per slice.
      const position = trace['textposition'];
      const positions = Array.isArray(position) ? position : [position];
      return positions.some((p) => typeof p === 'string' && p.includes('outside'));
    });
  }

  /**
   * Whether a trace's own text already names each slice, making a legend a second copy.
   *
   * Position alone is not enough to go on. A pie labelled `textinfo: 'percent'` puts bare
   * numbers next to the slices and the legend is the only place the categories are named —
   * dropping it there would delete the one thing that makes the chart readable.
   */
  private namesItsSlices(trace: Record<string, unknown>): boolean {
    const textinfo = trace['textinfo'];
    if (typeof textinfo === 'string') return textinfo.includes('label');

    // A texttemplate overrides textinfo, and names the slice through this placeholder.
    const template = trace['texttemplate'];
    const templates = Array.isArray(template) ? template : [template];
    return templates.some((t) => typeof t === 'string' && t.includes('%{label}'));
  }

  /** The figure's title, which Plotly accepts either as a bare string or as `{ text }`. */
  private titleText(layout: Record<string, unknown>): string {
    const title = layout['title'];
    if (typeof title === 'string') return title;

    const text = (title as Record<string, unknown> | undefined)?.['text'];
    return typeof text === 'string' ? text : '';
  }

  /**
   * Put the figure's title above the chart as ordinary markup, and take it out of the figure.
   *
   * Only reached for a pie with outside text, and only because nothing inside the SVG works.
   * The title is drawn in the top margin, the outside labels overflow through that same margin
   * and past the paper, and the two collide. Growing `margin.t` moves both down together;
   * `title.automargin` lifts the title until it leaves the canvas; pinning it to the container
   * top, left-aligning it, and shrinking the trace domain each fix some window widths and fail
   * at others. There is no band inside the SVG that the labels do not reach.
   *
   * Every other chart keeps its title where it was, so its PNG export is unchanged. For this
   * one shape the export loses the title — but a title it could place cleanly does not exist,
   * which is the whole reason this method is here.
   */
  private mountTitle(el: HTMLElement, text: string): void {
    const parent = el.parentElement;
    if (!parent) return;

    // Charts are re-rendered in place as an answer streams, so this has to update the heading
    // it already added rather than stack a new one above it each time.
    const existing = el.previousElementSibling;
    if (existing?.classList.contains('plotly-title')) {
      existing.textContent = text;
      return;
    }

    const heading = document.createElement('div');
    heading.className = 'plotly-title';
    heading.textContent = text;
    parent.insertBefore(heading, el);
  }

  /**
   * The height the chart box gets, from the height the figure asked for.
   *
   * Two ceilings apply. `MAX_CHART_HEIGHT` is about the chart: past it a figure is not being
   * read, it is being scrolled. The viewport share is about the conversation: on a laptop the
   * absolute cap alone still yields a chart that fills the screen on its own, which is what
   * makes it read as a page rather than as one message. The lower of the two wins, and the
   * minimum wins over both so a short window cannot collapse the box.
   */
  private boxHeight(declared: number): number {
    const viewportCap = Math.round(window.innerHeight * MAX_VIEWPORT_SHARE);
    const ceiling = Math.max(MIN_CHART_HEIGHT, Math.min(MAX_CHART_HEIGHT, viewportCap));
    return Math.min(Math.max(declared, MIN_CHART_HEIGHT), ceiling);
  }

  /**
   * Reconcile the figure's own sizing with the box it has to live in.
   *
   * The layout comes out of model-written Python, so it carries whatever dimensions that code
   * asked for — often a height sized for a full page. The container is a fixed 450px tall and
   * clips what overflows, so a tall figure used to render as a band sliced out of its middle,
   * with the title, the legend and half the labels cut away.
   *
   * The height is therefore clamped and *written back*: Plotly lays out to `layout.height`, so
   * clamping the box without clamping the figure would only move the same clipping inside the
   * SVG. Within the bounds the figure keeps the height it asked for, which is what lets a
   * 27-slice pie be taller than a two-bar chart.
   */
  private fitLayout(
    spec: { layout?: unknown; data?: unknown },
    el: HTMLElement,
  ): { layout: Record<string, unknown>; title: string } {
    const layout: Record<string, unknown> = { ...((spec.layout as Record<string, unknown>) ?? {}) };

    const outsidePies = this.outsideTextPies(spec.data);

    // A pie that labels its slices outside the circle and also draws a legend is saying the
    // same thing twice in the same place: Plotly puts outside labels and the legend both in
    // the strip to the right of the plot, so the legend sits on top of the labels and cuts
    // the longer country names in half. The labels win — they carry the percentage as well as
    // the name, so dropping the legend loses nothing. The chart prompt asks for one or the
    // other, but figures already stored in conversation history were written before it did.
    if (layout['showlegend'] !== false && outsidePies.some((trace) => this.namesItsSlices(trace))) {
      layout['showlegend'] = false;
    }

    // The same overflow puts the labels through the title's band, so for this shape the title
    // is lifted out of the figure and drawn above it as markup instead. Taking it out of the
    // layout also frees the top margin Plotly was holding for it.
    const title = outsidePies.length > 0 ? this.titleText(layout) : '';
    if (title) {
      delete layout['title'];
      layout['margin'] = { t: 30, ...((layout['margin'] as Record<string, unknown>) ?? {}) };
    }

    const declared = typeof layout['height'] === 'number' ? (layout['height'] as number) : DEFAULT_CHART_HEIGHT;
    const height = this.boxHeight(declared);

    layout['height'] = height;
    // Plotly's default modebar is a 30%-opacity watermark, on the assumption that a page full
    // of charts should not be a page full of toolbars. This chart has two controls and they
    // are the only way to export or expand it, so they are drawn as controls. It has to go
    // through the layout rather than through CSS: Plotly renders the bar from a stylesheet it
    // generates per chart under a generated id, which no rule in a component stylesheet can
    // outrank. A figure that set its own modebar colours still wins.
    layout['modebar'] = {
      bgcolor: 'rgba(255, 255, 255, 0.85)',
      color: 'rgba(71, 85, 105, 0.8)',
      activecolor: '#009edb',
      ...((layout['modebar'] as Record<string, unknown>) ?? {}),
    };
    // A declared width pins the figure to a pixel size that knows nothing about the bubble it
    // landed in — too narrow leaves dead space, too wide gets clipped. Dropping it hands the
    // width back to `responsive: true` and the container.
    delete layout['width'];
    layout['autosize'] = true;

    // The element is what the skeleton and any previous render were sized at, so it has to
    // follow the figure rather than stay on the stylesheet's default.
    el.style.height = `${height}px`;

    return { layout, title };
  }

  async renderDiagrams(container: HTMLElement | null): Promise<void> {
    if (!container) return;

    const elements = container.querySelectorAll('.plotly-diagram[data-plotly-src]');
    if (elements.length === 0) return;

    try {
      await this.ensurePlotlyLoaded();
    } catch (error) {
      console.warn('Plotly unavailable:', error);
      return;
    }
    if (!this.Plotly) return;

    for (const el of Array.from(elements) as HTMLElement[]) {
      const encoded = el.getAttribute('data-plotly-src') ?? '';
      if (!encoded) continue;

      // Remove immediately so concurrent ngAfterViewChecked calls skip this element.
      el.removeAttribute('data-plotly-src');

      try {
        const fence = JSON.parse(decodeURIComponent(encoded));
        const spec = await this.resolveSpec(fence);

        // Kept on the element so the chat component can offer to pin this chart in the
        // composer. Only charts that live behind an id can be pinned: a figure inlined in
        // an old answer has nothing the agent could be pointed at. The title comes from the
        // figure rather than from the fence, which carries the id and nothing else.
        if (fence?.chart_id) {
          el.dataset['chartId'] = fence.chart_id;
          const title = spec?.layout?.title?.text;
          if (typeof title === 'string' && title) el.dataset['chartTitle'] = title;
        }
        const { layout, title } = this.fitLayout(spec, el);
        if (title) this.mountTitle(el, title);

        // Drop the loading skeleton before drawing — Plotly doesn't clear
        // pre-existing DOM content that it doesn't recognize as its own.
        el.innerHTML = '';
        // Purge any previous plot state left on this element before drawing the
        // new one — newPlot alone can leave stale layout sizing behind when the
        // container was resized/hidden between renders.
        this.Plotly.purge(el);
        await this.Plotly.newPlot(el, spec.data ?? [], layout, {
          responsive: true,
          displayModeBar: true,
          displaylogo: false,
          modeBarButtonsToAdd: [this.expandButton()],
          // The default export is a screenshot at CSS pixel size, which looks soft the moment
          // it leaves the browser. The chart is often the thing being taken away to a slide.
          toImageButtonOptions: { format: 'png', scale: 2 },
        });
        this.bindFullscreen(el, layout['height'] as number);
        this.Plotly.Plots.resize(el);
      } catch (error) {
        console.warn('Failed to render Plotly diagram:', error);
        el.innerHTML = '<span class="plotly-error">Failed to render chart.</span>';
      }
    }
  }
}
